import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumberParam, useQueryParams } from 'use-query-params'

import translationKeys from '@translationKeys'

import CenteredFlexRow from '@components/CenteredFlexRow'
import Redirect from '@components/Redirect'
import SelectWithLabeledValue, { SelectOption } from '@components/SelectWithLabeledValue'
import { NewCountAssetsBadge } from '@components/SelectWithLabeledValue/index.styles'
import alertsApi from '@modules/alerts/api'
import useAlertDataForProfiles from '@modules/alerts/hooks/useAlertDataForProfiles'
import { POneEvent } from '@modules/auth/hooks/types'
import searchApi from '@modules/search/api'
import { FetchEventGroupedAssetProfilesFnParams, FetchEventGroupedAssetProfilesFnResponse } from '@modules/search/api/profile'
import ResultProfileList from '@modules/search/components/ResultsSection/components/ResultProfileList'
import { Company, ProfileType } from '@modules/search/types'
import { PaginationItem, PaginationItemProps, Tab } from '@mui/material'
import Box from '@mui/material/Box'
import Pagination from '@mui/material/Pagination'
import Tabs from '@mui/material/Tabs'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { truncateWithDots } from '@shared/utils'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import CompanyCard from '../CompanyCard'
import useAssetTabs from '../hooks/useAssetTabs'
import {
  NewAssetsBadge,
  ProfileEmptySection,
  ProfileEmptySectionMessage,
  ProfileName,
  ProfileOuterWrapper,
  ProfileWrapper,
} from '../index.styles'
import LoadingSkeleton from './LoadingSkeleton'
import { AssetsOverviewSubSectionTitle, SelectDelimeter } from './index.styles'

export interface AssetsOverviewProps {
  companyId?: Company['id']
  eventId?: POneEvent['id']
}

const DEFAULT_PAGE_LIMIT = 20

const AssetsOverview = ({ companyId, eventId: rawEventId }: AssetsOverviewProps) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const queryClient = useQueryClient()
  const markViewedRefTimeout = useRef(0)
  const [selectedEventId, setSelectedEventId] = useState<number | undefined>()
  const { hasAccessToAlerts } = useFeatureFlags()

  const { data: viewedAssetsData, isLoading: isViewedAssetsLoading } = useQuery({
    queryKey: [alertsApi.fetchViewedAssets.key, companyId],
    queryFn: () => alertsApi.fetchViewedAssets.fn({ companyId }),
    placeholderData: keepPreviousData,
    enabled: hasAccessToAlerts && !!companyId,
  })

  const { data: { events: eventsWithAssets = [] } = {} } = useQuery({
    queryKey: [searchApi.getFilterForAllAssetsPage.key, companyId, viewedAssetsData],
    queryFn: () =>
      searchApi.getFilterForAllAssetsPage.fn({
        companyId: companyId as number,
        viewedAssetsData,
      }),
    placeholderData: keepPreviousData,
    enabled: !isViewedAssetsLoading && !!companyId,
  })

  const totalNewAssets = useMemo(
    () => (hasAccessToAlerts ? eventsWithAssets.reduce((acc, event) => acc + (event?.newAssetsCount ?? 0), 0) : 0),
    [eventsWithAssets, hasAccessToAlerts],
  )

  const [query, setQuery] = useQueryParams({
    event_id: NumberParam,
  })

  useEffect(() => {
    if (!query.event_id) {
      setQuery({ event_id: null }, 'replace')
      return
    }

    setSelectedEventId(query.event_id)
    setQuery({ event_id: null }, 'replace')
  }, [query.event_id])

  const eventId = useMemo(() => {
    if (typeof selectedEventId === 'number') {
      if (eventsWithAssets?.some(i => i.id === selectedEventId)) {
        return selectedEventId
      }
    }

    if (eventsWithAssets?.some(i => i.id === rawEventId)) {
      return rawEventId
    }

    const sortedEvents = eventsWithAssets?.sort((eventA, eventB) => {
      const dateA = new Date(eventA.endDate || eventA.startDate).getTime()
      const dateB = new Date(eventB.endDate || eventB.startDate).getTime()
      return dateB - dateA
    })

    return sortedEvents?.[0]?.id
  }, [eventsWithAssets, rawEventId, selectedEventId])

  useEffect(() => {
    if (!selectedEventId && eventsWithAssets?.length > 0) {
      setSelectedEventId(eventsWithAssets[0].id)
    }
  }, [selectedEventId, eventsWithAssets])

  const { key: apiKey, fn: apiFn } = searchApi.getEventGroupedCompanyAssetProfiles

  const {
    activeTab,
    setActiveTab,
    tabs,
    facets,
    isLoading: isFacetsLoading,
  } = useAssetTabs<FetchEventGroupedAssetProfilesFnParams, FetchEventGroupedAssetProfilesFnResponse>({
    apiKey,
    apiFn,
    companyId,
    t,
    codes: eventId ? [eventId] : [],
  })

  const { data: { result, newAssetCounts } = {}, isLoading } = useQuery({
    queryKey: [apiKey, companyId, activeTab, page, eventId, viewedAssetsData],
    queryFn: () =>
      apiFn({
        companyId: companyId as number,
        from: DEFAULT_PAGE_LIMIT * (page - 1),
        size: DEFAULT_PAGE_LIMIT,
        types: [{ type: activeTab as ProfileType }],
        codes: eventId ? [eventId] : [],
        ...(viewedAssetsData ?? {}),
      }),
    placeholderData: keepPreviousData,
    enabled: !isViewedAssetsLoading && !!facets && !!activeTab && typeof eventId === 'number',
  })

  const newProfilesMap = useMemo(() => {
    if (!viewedAssetsData?.alertCreatedAt) {
      return {}
    }

    const alertSinceTime = new Date(viewedAssetsData.alertCreatedAt).getTime()
    const assets = result?.flatMap(({ items }) => items)

    return assets?.reduce((acc, asset) => {
      if (alertSinceTime > new Date(asset.latest.createdAt).getTime()) {
        return acc
      }

      if (viewedAssetsData?.viewedAssets?.includes(String(asset.latest.id))) {
        return acc
      }

      return {
        ...acc,
        [asset.latest.id]: true,
      }
    }, {} as Record<string, boolean>)
  }, [viewedAssetsData, result])

  const { mutate: markProfilesAsViewed } = useMutation({
    mutationFn: alertsApi.updateViewedAssets.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [alertsApi.fetchViewedAssets.key, companyId],
      })
      queryClient.invalidateQueries({
        queryKey: [searchApi.getEventGroupedCompanyAssetProfiles.key, companyId, activeTab, page, eventId, viewedAssetsData],
      })
      queryClient.invalidateQueries({
        queryKey: [searchApi.getFilterForAllAssetsPage.key, companyId, viewedAssetsData],
      })
    },
  })

  useEffect(() => {
    if (!viewedAssetsData?.alertCreatedAt) {
      return
    }

    markViewedRefTimeout.current = new Date().getTime()

    // eslint-disable-next-line
    return () => {
      if (companyId && new Date().getTime() - markViewedRefTimeout.current > 3000) {
        markProfilesAsViewed({
          companyId,
          viewedAssets: result?.flatMap(({ items }) => items.map(item => `${item.id}`)) || [],
        })
      }
    }
  }, [markViewedRefTimeout, companyId, viewedAssetsData, result])

  const handleProfileNavigate = useCallback(
    (assetId: string | number) => {
      if (!viewedAssetsData?.alertCreatedAt || !companyId) {
        return
      }

      markProfilesAsViewed({
        companyId,
        viewedAssets: [assetId as string],
      })
    },
    [companyId, viewedAssetsData],
  )

  const filterByEventOptions = useMemo(() => {
    if (!eventsWithAssets) {
      return []
    }

    return eventsWithAssets
      .sort(
        (eventA, eventB) =>
          new Date(eventB.endDate || eventB.startDate).getTime() - new Date(eventA.endDate || eventA.startDate).getTime(),
      )
      .map((e: POneEvent) => ({
        id: e.id as number,
        label: e.name,
        data: e,
        badgeContent: (e.newAssetsCount ?? 0) > 99 ? '99+' : e.newAssetsCount,
      }))
  }, [eventsWithAssets])

  const { profilesAlertsMap } = useAlertDataForProfiles(
    result?.flatMap(({ items }) => items),
    !!(hasAccessToAlerts && result?.length),
  )

  const total = activeTab && facets?.[activeTab]
  const totalPages = total ? Math.ceil(total / DEFAULT_PAGE_LIMIT) : 0

  const handlePaginationChange = (e: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage)

    if (window.scrollY > 250) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const renderSelectedEventFilter = useCallback(
    (selectedOption: SelectOption) => {
      return (
        <Box display="flex" alignItems="center" flexDirection="row">
          {t(translationKeys.ALL_PRODUCTS_FILTER_BY_EVENT_LABEL)}
          {selectedOption?.id ? (
            <>
              <SelectDelimeter />
              {truncateWithDots(selectedOption.label, 30)}
              {totalNewAssets > 0 && (
                <NewCountAssetsBadge style={{ height: '8px' }}>
                  {totalNewAssets > 99 ? '99+' : totalNewAssets}
                </NewCountAssetsBadge>
              )}
            </>
          ) : (
            ''
          )}
        </Box>
      )
    },
    [totalNewAssets],
  )

  const renderPaginationItem = (item: PaginationItemProps) => {
    const { page: paginationPage, type } = item
    const newAssetsCount = newAssetCounts?.perPage?.[paginationPage as number] ?? 0

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
        {/* eslint-disable-next-line */}
        <PaginationItem {...item} />
        {newAssetsCount > 0 && type === 'page' && (
          <NewAssetsBadge
            sx={{
              position: 'absolute',
              top: -10,
              right: -8,
            }}
          >
            {newAssetsCount > 99 ? '99+' : newAssetsCount}
          </NewAssetsBadge>
        )}
      </Box>
    )
  }

  const handleSelectEvent = useCallback((id: number | string) => {
    setSelectedEventId(id as number)
    setPage(1)
    setActiveTab(null)
  }, [])

  const handleTabChange = useCallback((_: unknown, tab: ProfileType) => {
    setPage(1)
    setActiveTab(tab)
  }, [])

  if (!facets && !isFacetsLoading) {
    return companyId ? <Redirect to={`/plus/profile/${companyId}/company`} /> : null
  }

  return (
    <ProfileOuterWrapper>
      <ProfileWrapper>
        {!isLoading ? (
          <Box width="100%" mb="30px">
            <Box>
              <CenteredFlexRow justifyContent="space-between">
                <ProfileName variant="h1">{t(translationKeys.ALL_PRODUCT_AND_SERVICES_TITLE)}</ProfileName>
                <SelectWithLabeledValue
                  label={t(translationKeys.ALL_PRODUCTS_FILTER_BY_EVENT_LABEL)}
                  onSelect={handleSelectEvent}
                  options={filterByEventOptions}
                  selectedOptionId={eventId}
                  renderSelectedState={renderSelectedEventFilter}
                  emptySelection
                  emptySelectionLabel={translationKeys.ALL_PRODUCTS_FILTER_BY_EVENT_LABEL}
                />
              </CenteredFlexRow>
              <Tabs value={activeTab || tabs[0]} onChange={handleTabChange}>
                {tabs.map(tab => (
                  <Tab
                    key={tab.key}
                    value={tab.key}
                    label={
                      <Box display="flex" alignItems="center">
                        {tab.label}
                        {(newAssetCounts?.facets?.[tab.key] ?? 0) > 0 && (
                          <NewAssetsBadge>
                            {(newAssetCounts?.facets?.[tab.key] ?? 0) > 99 ? '99+' : newAssetCounts?.facets?.[tab.key]}
                          </NewAssetsBadge>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>

            {result?.length ? (
              result.map(({ items, event }) => (
                <Box key={event?.id}>
                  <AssetsOverviewSubSectionTitle variant="h2">{event?.name}</AssetsOverviewSubSectionTitle>
                  <ResultProfileList
                    newProfilesMap={newProfilesMap}
                    profiles={items}
                    profilesAlertsMap={profilesAlertsMap}
                    onProfileNavigate={handleProfileNavigate}
                  />
                </Box>
              ))
            ) : (
              <ProfileEmptySection>
                <ProfileEmptySectionMessage variant="h3">
                  {t(translationKeys.ALL_PRODUCTS_EMPTY_ASSETS_MESSAGE)}
                </ProfileEmptySectionMessage>
              </ProfileEmptySection>
            )}

            {totalPages > 1 && (
              <Pagination count={totalPages} page={page} onChange={handlePaginationChange} renderItem={renderPaginationItem} />
            )}
          </Box>
        ) : (
          <LoadingSkeleton />
        )}

        {companyId && <CompanyCard companyId={companyId} />}
      </ProfileWrapper>
    </ProfileOuterWrapper>
  )
}

export default memo(AssetsOverview)
